import React, { ReactNode, useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { AccessInput } from '@/abilities/accessInput';
import type { RouteCondition } from '@/abilities/routeCondition';
import { showApiErrorToast } from '@/services/api/errorToast';
import { useIsCompanyScopeBehind } from '@/hooks/useIsCompanyScopeBehind';
import Route404 from '@/pages/servicePage/page404';

type RouteGateProps = {
  canOpen: RouteCondition;
  children: ReactNode;
};

const loadingBar = (
  <Box sx={{ width: '100%' }}>
    <LinearProgress />
  </Box>
);

const RouteGate = ({ canOpen, children }: RouteGateProps) => {
  const isScopeBehind = useIsCompanyScopeBehind();
  const access = canOpen();
  const error = !isScopeBehind && AccessInput.$is('Failed')(access) ? access.error : null;

  // A route that cannot open must not wait silently: the user sees why.
  useEffect(() => {
    if (error) showApiErrorToast(error);
  }, [error]);

  if (isScopeBehind) return loadingBar;
  return AccessInput.$match(access, {
    Loading: () => loadingBar,
    Failed: () => loadingBar,
    Ready: ({ value }) => (value ? <>{children}</> : <Route404 />),
  });
};

export default RouteGate;
